import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, X, LogOut, Shield, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Emergency Exit - Limpia todo y regresa al login
 * Usar cuando el sistema está atascado
 */
export const emergencyExit = () => {
  localStorage.clear();
  sessionStorage.clear();
  delete axios.defaults.headers.common["Authorization"];
  window.location.href = '/login';
};

/**
 * ImpersonationBanner - Shows when admin is viewing as another user
 * Props:
 * - user: Current user object with is_impersonated and impersonated_by
 * - onStopImpersonation: Callback when admin stops impersonating
 */
export const ImpersonationBanner = ({ user, onStopImpersonation }) => {
  const [loading, setLoading] = useState(false);
  const [showEmergency, setShowEmergency] = useState(false);

  // Mostrar botón de emergencia después de 3 segundos si está en modo impersonación
  useEffect(() => {
    if (user?.is_impersonated) {
      const timer = setTimeout(() => setShowEmergency(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [user?.is_impersonated]);

  if (!user?.is_impersonated) return null;

  const handleStopImpersonation = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/stop-impersonation`);
      
      // Update token and user in localStorage
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      
      // Set axios default header
      axios.defaults.headers.common["Authorization"] = `Bearer ${response.data.token}`;
      
      toast.success(response.data.message);
      
      // Call callback to refresh user state
      if (onStopImpersonation) {
        onStopImpersonation(response.data);
      }
      
      // Redirect to admin dashboard
      window.location.href = "/admin";
    } catch (error) {
      console.error("Error stopping impersonation:", error);
      toast.error("Error al salir. Usa 'Salida de Emergencia' si persiste.");
      setShowEmergency(true);
    } finally {
      setLoading(false);
    }
  };

  const handleEmergencyExit = () => {
    if (window.confirm("¿Salida de emergencia?\n\nEsto cerrará tu sesión completamente y tendrás que iniciar sesión de nuevo.")) {
      emergencyExit();
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -100, opacity: 0 }}
        className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white shadow-lg"
      >
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white/20 rounded-full px-3 py-1">
              <Eye className="w-4 h-4" />
              <span className="font-medium text-sm">MODO VISTA</span>
            </div>
            <div className="hidden sm:block">
              <span className="text-white/80 text-sm">Estás viendo como:</span>
              <span className="font-bold ml-2">{user.name}</span>
              <span className="text-white/70 ml-1">({user.role})</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2 text-sm text-white/80">
              <Shield className="w-4 h-4" />
              Admin: {user.impersonated_by?.name}
            </div>
            
            {/* Botón principal para volver */}
            <Button
              onClick={handleStopImpersonation}
              disabled={loading}
              variant="secondary"
              size="sm"
              className="bg-white text-orange-600 hover:bg-white/90 font-semibold"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-orange-600 border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <LogOut className="w-4 h-4 mr-2" />
                  Volver a Admin
                </>
              )}
            </Button>
            
            {/* Botón de emergencia - aparece después de 3 segundos o si hay error */}
            {showEmergency && (
              <Button
                onClick={handleEmergencyExit}
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20"
                title="Salida de emergencia - Limpia todo y regresa al login"
              >
                <AlertTriangle className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * ImpersonateButton - Button to start impersonating a user
 * Props:
 * - userId: ID of user to impersonate
 * - userName: Name of user to display in confirmation
 * - userRole: Role of user for proper redirect
 * - onSuccess: Callback on successful impersonation
 */
export const ImpersonateButton = ({ userId, userName, userRole, onSuccess, size = "sm" }) => {
  const [loading, setLoading] = useState(false);

  const handleImpersonate = async () => {
    if (!window.confirm(`¿Quieres ver la plataforma como ${userName}?\n\nPodrás ver exactamente lo que ve este usuario.\n\nSi algo sale mal, usa el botón de emergencia (⚠️) en el banner amarillo.`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/impersonate`, {
        user_id: userId
      });
      
      // Update token and user in localStorage
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify({
        ...response.data.user,
        is_impersonated: true,
        impersonated_by: response.data.impersonated_by
      }));
      
      // Set axios default header
      axios.defaults.headers.common["Authorization"] = `Bearer ${response.data.token}`;
      
      toast.success(response.data.message);
      
      if (onSuccess) {
        onSuccess(response.data);
      }
      
      // Redirect based on role
      const redirectPath = userRole === "oficial" ? "/oficial" : 
                          userRole === "vendedor" ? "/vendedor" : "/dashboard";
      window.location.href = redirectPath;
    } catch (error) {
      console.error("Error impersonating:", error);
      toast.error(error.response?.data?.detail || "Error al impersonar usuario");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={handleImpersonate}
      disabled={loading}
      variant="outline"
      size={size}
      className="text-amber-600 border-amber-200 hover:bg-amber-50"
      title={`Ver plataforma como ${userName}`}
    >
      {loading ? (
        <div className="w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
      ) : (
        <>
          <Eye className="w-4 h-4 mr-1" />
          Ver como
        </>
      )}
    </Button>
  );
};

export default ImpersonationBanner;
